import { useMemo, useState } from 'react';

import PermMediaRoundedIcon from '@mui/icons-material/PermMediaRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import UploadRoundedIcon from '@mui/icons-material/UploadRounded';
import { Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { EmptyState, SectionScaffold } from '@/shared/ui';

/**
 * Media section — the media library for the active tenant.
 *
 * Promoted to a standalone section (out of the settings hub): media is an asset
 * workspace, not a one-off configuration screen. The asset grid (search + upload)
 * will render here once the media data API is wired; the gear opens the media
 * rules (formats, variants, CDN).
 */
export function MediaPage() {
  const { hasPermission } = usePermissions();
  const { tenantName } = useTenant();
  const [search, setSearch] = useState('');

  const canView = hasPermission('settings.media.view');
  const tenantLabel = useMemo(() => tenantName ?? 'this store', [tenantName]);

  return (
    <SectionScaffold
      title="Media"
      subtitle="Manage images, banners and assets for this store."
      canView={canView}
      settingsTo="/settings/media"
      settingsLabel="Media settings"
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search media by file name or tag…',
        disabled: true,
        disabledHint: 'Media search becomes available once the media library is connected.',
      }}
      primaryAction={
        <Button variant="contained" startIcon={<UploadRoundedIcon />} disabled>
          Upload
        </Button>
      }
    >
      <EmptyState
        icon={<PermMediaRoundedIcon sx={{ fontSize: 48 }} />}
        title="Media library is coming soon"
        description={`Images and banners for ${tenantLabel} will live here. In the meantime, configure image formats, variants, banners and the CDN.`}
        action={
          <Button
            variant="outlined"
            component={RouterLink}
            to="/settings/media"
            startIcon={<SettingsRoundedIcon />}
          >
            Open media settings
          </Button>
        }
      />
    </SectionScaffold>
  );
}
